/**
 * Redux slice du graphe notes ↔ entrées (chantier C7, vue « Graphe »).
 *
 * Porte les données de `GET /api/graph` pour la portée affichée (un périmètre
 * ou toute la base), sur le modèle de `noteBacklinksSlice`. Garde anti-course :
 * un résultat qui n'appartient plus à la portée courante (changement de
 * périmètre pendant le chargement) est ignoré. Les filtres d'affichage (types,
 * arêtes, isolés, recherche) restent locaux à la vue.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { graphService } from '../../services/graphAPI';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import { LOADING_STATES } from '../../constants';
import type { LoadingState } from '../../types/redux';
import type { Foyer, GraphEdge, GraphNode, KnowledgeGraphData, KnowledgeGraphTotals } from '../../types/api';
import { ajouterFoyer, foyerKey, retirerFoyer } from '../../utils/graph/foyers';

export interface KnowledgeGraphState {
    /** Clé de la portée dont parlent les données (`all` ou l'id du périmètre) ; null = rien chargé. */
    scopeKey: string | null;
    perimetreId: string | null;
    nodes: GraphNode[];
    edges: GraphEdge[];
    totals: KnowledgeGraphTotals;
    truncated: boolean;
    groupesSansConcept: string[][];
    status: LoadingState;
    error: string | null;
    /** Les foyers choisis dans la section « Graphe » (19/10/2026) ; ils survivent au démontage de la section. */
    foyersChoisis: Foyer[];
}

const EMPTY_TOTALS: KnowledgeGraphTotals = { notes: 0, entrees: 0, edges: 0 };
const EMPTY_NODES: GraphNode[] = [];
const EMPTY_EDGES: GraphEdge[] = [];
const EMPTY_GROUPES: string[][] = [];

export const scopeKeyOf = (perimetreId: string | null | undefined): string => perimetreId || 'all';

/** Clé des foyers (19/10/2026) : la même quel que soit l'ordre de choix ; `null` sans foyer. */
export const foyersKeyOf = (foyers: ReadonlyArray<string> | null | undefined): string | null =>
    (foyers && foyers.length > 0 ? `foyers:${Array.from(new Set(foyers)).sort().join(',')}` : null);

export interface FetchKnowledgeGraphArg {
    perimetreId?: string | null;
    /** Avec au moins un foyer, la clé de portée est celle des foyers (`perimetreId` compte alors comme un foyer de plus). */
    foyers?: string[];
    instance: any;
}

/** La clé de portée d'une demande : les foyers s'ils existent, sinon le périmètre (ou toute la base). */
export const requestKeyOf = (arg: { perimetreId?: string | null; foyers?: ReadonlyArray<string> }): string =>
    foyersKeyOf(arg.foyers) ?? scopeKeyOf(arg.perimetreId);

export const initialKnowledgeGraphState: KnowledgeGraphState = {
    scopeKey: null,
    perimetreId: null,
    nodes: [],
    edges: [],
    totals: EMPTY_TOTALS,
    truncated: false,
    groupesSansConcept: [],
    status: LOADING_STATES.IDLE,
    error: null,
    foyersChoisis: [],
};

export const fetchKnowledgeGraph = createAsyncThunk<KnowledgeGraphData, FetchKnowledgeGraphArg>(
    'knowledgeGraph/fetch',
    async ({ perimetreId, foyers, instance }) => {
        const accessToken = await getTokenRedirect(tokenRequest, instance);
        if (!accessToken) {
            throw new Error('Impossible d\'obtenir le token d\'accès');
        }
        return graphService.getKnowledgeGraph({ perimetreId: perimetreId ?? null, ...(foyers && foyers.length > 0 ? { foyers } : {}) }, accessToken);
    }
);

const knowledgeGraphSlice = createSlice({
    name: 'knowledgeGraph',
    initialState: initialKnowledgeGraphState,
    reducers: {
        resetKnowledgeGraph() {
            return initialKnowledgeGraphState;
        },
        /** Un foyer de plus dans la section (sans doublon). */
        ajouterFoyerChoisi(state, action: { payload: Foyer }) {
            state.foyersChoisis = ajouterFoyer(state.foyersChoisis, action.payload);
        },
        /** Un foyer de moins, par sa clé `kind:id`. */
        retirerFoyerChoisi(state, action: { payload: string }) {
            state.foyersChoisis = retirerFoyer(state.foyersChoisis, action.payload);
        },
        viderFoyersChoisis(state) {
            state.foyersChoisis = [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchKnowledgeGraph.pending, (state, action) => {
                const key = requestKeyOf(action.meta.arg);
                if (state.scopeKey !== key) {
                    // Nouvelle portée : ne pas montrer le graphe de la précédente pendant le chargement.
                    state.nodes = [];
                    state.edges = [];
                    state.totals = EMPTY_TOTALS;
                    state.truncated = false;
                    state.groupesSansConcept = [];
                }
                state.scopeKey = key;
                state.perimetreId = action.meta.arg.perimetreId || null;
                state.status = LOADING_STATES.LOADING;
                state.error = null;
            })
            .addCase(fetchKnowledgeGraph.fulfilled, (state, action) => {
                if (requestKeyOf(action.meta.arg) !== state.scopeKey) return;
                state.nodes = action.payload?.nodes ?? [];
                state.edges = action.payload?.edges ?? [];
                state.totals = action.payload?.totals ?? EMPTY_TOTALS;
                state.truncated = action.payload?.truncated ?? false;
                state.groupesSansConcept = action.payload?.groupesSansConcept ?? [];
                state.status = LOADING_STATES.SUCCEEDED;
                state.error = null;
            })
            .addCase(fetchKnowledgeGraph.rejected, (state, action) => {
                if (requestKeyOf(action.meta.arg) !== state.scopeKey) return;
                state.status = LOADING_STATES.FAILED;
                state.error = action.error?.message || 'Impossible de charger le graphe';
            });
    },
});

export const { resetKnowledgeGraph, ajouterFoyerChoisi, retirerFoyerChoisi, viderFoyersChoisis } = knowledgeGraphSlice.actions;

// Sélecteurs nul-sûrs : un store de test peut ne pas porter ce slice.
const sliceOf = (state: any): KnowledgeGraphState | undefined => state?.knowledgeGraph;

export const selectGraphScopeKey = (state: any): string | null => sliceOf(state)?.scopeKey ?? null;
export const selectGraphStatus = (state: any): LoadingState => sliceOf(state)?.status ?? LOADING_STATES.IDLE;
export const selectGraphError = (state: any): string | null => sliceOf(state)?.error ?? null;
export const selectGraphNodes = (state: any): GraphNode[] => sliceOf(state)?.nodes ?? EMPTY_NODES;
export const selectGraphEdges = (state: any): GraphEdge[] => sliceOf(state)?.edges ?? EMPTY_EDGES;
export const selectGraphTotals = (state: any): KnowledgeGraphTotals => sliceOf(state)?.totals ?? EMPTY_TOTALS;
export const selectGraphTruncated = (state: any): boolean => sliceOf(state)?.truncated ?? false;
/** Groupes de notes reliées sans concept commun (C22 SP3). */
export const selectGraphGroupesSansConcept = (state: any): string[][] => sliceOf(state)?.groupesSansConcept ?? EMPTY_GROUPES;
const EMPTY_FOYERS: Foyer[] = [];
/** Les foyers choisis dans la section « Graphe » (19/10/2026). */
export const selectFoyersChoisis = (state: any): Foyer[] => sliceOf(state)?.foyersChoisis ?? EMPTY_FOYERS;
export { foyerKey };

export default knowledgeGraphSlice.reducer;
